/**
 * PublicHome: tenant-branded landing page.
 *
 * Entry points: draw a district plan, submit a community of interest.
 * The hero keeps it welcoming for first-time residents: plain language,
 * one clear next step per card, and a decorative district-map motif
 * (inline SVG, no external assets).
 */
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTenantStore } from '../stores/tenant';
import { BrushIcon, UsersIcon } from '../components/icons';

interface ActionCard {
  to: string;
  title: string;
  description: string;
  cta: string;
  Icon: typeof BrushIcon;
  accent: string;
}

/**
 * Hero illustration: a census-block grid painted into districts, with a
 * community-of-interest outline traced over it — the two things this site
 * lets people do, drawn in the same palette the map uses. Pure SVG.
 */
function DistrictMotif({ colors }: { colors: string[] }): JSX.Element {
  const cols = 9;
  const rows = 6;
  const size = 34;
  const gap = 3;
  // District index per cell (-1 = unassigned): four organic-looking regions.
  const layout = [
    '000111122',
    '000111122',
    '003311222',
    '333311-22',
    '3333---22',
    '33--------',
  ];
  const palette = [colors[0] ?? '#2F6DB5', colors[1] ?? '#E8912D', colors[2] ?? '#1E9E78', colors[3] ?? '#D1543A'];
  const cells: JSX.Element[] = [];
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const ch = layout[r]?.[c] ?? '-';
      const d = ch === '-' ? -1 : Number(ch);
      cells.push(
        <rect
          key={`${r}-${c}`}
          x={c * (size + gap)}
          y={r * (size + gap)}
          width={size}
          height={size}
          rx={4}
          fill={d >= 0 ? palette[d] : '#ffffff'}
          fillOpacity={d >= 0 ? 0.92 : 0.16}
        />,
      );
    }
  }
  const w = cols * (size + gap) - gap;
  const h = rows * (size + gap) - gap;
  return (
    <svg aria-hidden="true" viewBox={`-12 -12 ${w + 24} ${h + 24}`} className="h-full w-full">
      <g>{cells}</g>
      {/* A community of interest, traced across district lines. */}
      <path
        d={`M ${3.4 * (size + gap)} ${2.3 * (size + gap)} L ${6.2 * (size + gap)} ${2.0 * (size + gap)} L ${6.6 * (size + gap)} ${4.6 * (size + gap)} L ${4.1 * (size + gap)} ${5.2 * (size + gap)} Z`}
        fill="#ffffff"
        fillOpacity="0.18"
        stroke="#ffffff"
        strokeWidth="3"
        strokeDasharray="7 5"
        strokeLinejoin="round"
      />
      {[
        [3.4, 2.3],
        [6.2, 2.0],
        [6.6, 4.6],
        [4.1, 5.2],
      ].map(([x, y], i) => (
        <circle key={i} cx={(x ?? 0) * (size + gap)} cy={(y ?? 0) * (size + gap)} r={5} fill="#ffffff" />
      ))}
    </svg>
  );
}

const ACTIONS: ActionCard[] = [
  {
    to: '/map',
    title: 'Draw a district plan',
    description:
      'Paint census blocks into districts on an interactive map. Population totals update live as you draw, and you can check your plan against the legal requirements before submitting.',
    cta: 'Start drawing',
    Icon: BrushIcon,
    accent: '#2F6DB5',
  },
  {
    to: '/coi',
    title: 'Share your community of interest',
    description:
      'Tell us about the neighborhood or community you belong to — in your own words, and on the map if you like — so the people drawing districts keep it together.',
    cta: 'Describe your community',
    Icon: UsersIcon,
    accent: '#7B3F8C',
  },
];

export function PublicHome(): JSX.Element {
  const tenant = useTenantStore((s) => s.tenant);
  const name = tenant?.name ?? 'CivicLines';
  const tagline =
    tenant?.tagline ?? 'Help shape fair representation for your community.';

  // Footer links like "/#how-it-works": scroll to the section once rendered.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash]);

  // Keep document title in sync with the tenant brand.
  document.title = tenant ? `${name} · CivicLines` : 'CivicLines — Public redistricting participation';

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl bg-tenant-primary text-white shadow-float">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />
        <div className="relative grid gap-8 p-8 sm:p-12 lg:grid-cols-[1.15fr_1fr] lg:items-center">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-white/85">
              <span className="h-1.5 w-1.5 rounded-full bg-tenant-accent" aria-hidden="true" />
              Public redistricting · {name}
            </p>
            <h1 className="mt-5 text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl">
              Your community.
              <br />
              Your districts.
              <br />
              <span className="text-tenant-accent">Your voice.</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-white/80">{tagline}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/map" className="btn-accent px-5 py-3 text-base focus-visible:outline-white">
                <BrushIcon size={18} /> Draw a district plan
              </Link>
              <Link
                to="/coi"
                className="btn border border-white/40 px-5 py-3 text-base text-white hover:bg-white/10 focus-visible:outline-white"
              >
                <UsersIcon size={18} /> Share your community
              </Link>
            </div>
            <p className="mt-5 text-sm text-white/60">No account needed to start drawing.</p>
          </div>
          <div aria-hidden="true" className="relative mx-auto hidden w-full max-w-md lg:block">
            <DistrictMotif colors={tenant?.districts.colors ?? []} />
          </div>
        </div>
      </section>

      {/* Entry points */}
      <div className="mt-6 grid gap-6 sm:grid-cols-2">
        {ACTIONS.map((action) => (
          <Link
            key={action.to}
            to={action.to}
            className="card group p-7 transition hover:-translate-y-0.5 hover:border-tenant-primary/40 hover:shadow-float"
          >
            <span
              aria-hidden="true"
              className="flex h-11 w-11 items-center justify-center rounded-xl"
              style={{ backgroundColor: `${action.accent}1f`, color: action.accent }}
            >
              <action.Icon size={22} />
            </span>
            <h2 className="mt-4 text-xl font-bold text-ink group-hover:text-tenant-primary">
              {action.title}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-stone-600">{action.description}</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-tenant-primary">
              {action.cta}
              <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">
                →
              </span>
            </span>
          </Link>
        ))}
      </div>

      {/* How it works */}
      <section id="how-it-works" className="card mt-6 scroll-mt-24 p-7 sm:p-8">
        <h2 className="text-xl font-bold text-ink">How it works</h2>
        <ol className="mt-4 grid gap-6 sm:grid-cols-3">
          {[
            {
              n: '1',
              title: 'Explore',
              text: 'See your jurisdiction and its current district boundaries on the map.',
            },
            {
              n: '2',
              title: 'Draw or describe',
              text: 'Paint your own district plan, or tell us about your community of interest.',
            },
            {
              n: '3',
              title: 'Submit',
              text: 'Send it in for staff review — every submission gets a receipt and stays reproducible.',
            },
          ].map((step) => (
            <li key={step.n} className="flex gap-3">
              <span
                aria-hidden="true"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-tenant-tint text-sm font-bold text-tenant-strong"
              >
                {step.n}
              </span>
              <span>
                <span className="block text-sm font-bold text-ink">{step.title}</span>
                <span className="mt-1 block text-sm leading-relaxed text-stone-600">
                  {step.text}
                </span>
              </span>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
